import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { useVatRates } from '@features/vatRates/contexts/vatRatesContext'
import { useFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'

import { useFetchRecurringInvoiceDraftFormat } from '../../../../../recurringInvoices/routes/RecurringInvoiceView/hooks/useFetchRecurringInvoiceDraftFormat'
import { InvoiceEditorVariant } from '../../enums/invoiceEditorVariant'
import { getInvoiceEditorFormValuesFromDraft } from '../../utils/invoiceDraft/getInvoiceEditorFormValuesFromDraft'
import { getInvoiceFormDefaultValues } from '../../utils/invoiceEditorForm/getInvoiceFormDefaultValues'
import { getVatExemptedFormValues } from '../../utils/invoiceEditorForm/getVatExemptedFormValues'
import { getRecurringInvoiceFormDefaultValues } from '../../utils/recurringInvoice/getRecurringInvoiceFormDefaultValues'
import { getRecurringInvoiceFormValuesFromSchedule } from '../../utils/recurringInvoice/getRecurringInvoiceFormValuesFromSchedule'
import { useInvoiceEditorVariant } from '../useInvoiceEditorVariant'
import { useGetEditorValuesCommonData } from './useGetEditorValuesCommonData'

interface UseGetRecurringInvoiceEditorValuesOptions {
  // Switched off for every non-recurring editor variant, which loads its values through
  // useGetInvoiceEditorValues instead.
  // Both hooks are always called (see InvoiceEditorValuesProvider) so the disabled one
  // must not issue any requests.
  enabled: boolean
}

// Counterpart of useGetInvoiceEditorValues for the recurring invoice editor variants.
export const useGetRecurringInvoiceEditorValues = ({ enabled }: UseGetRecurringInvoiceEditorValuesOptions) => {
  const { t } = useTranslation()
  const {
    defaultContact,
    documentId,
    isLoading: isLoadingValuesData,
    isStripeCardPaymentActive,
    organization,
    predefinedInvoiceValues,
  } = useGetEditorValuesCommonData({ enabled })
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const { vatRates } = useVatRates()
  const { getFilteredVatRates } = useFilteredVatRates()
  const { recurringInvoice, isLoading: isLoadingRecurringInvoice } = useFetchRecurringInvoiceDraftFormat(
    enabled ? (documentId ?? '') : '',
  )

  const isLoading = isLoadingValuesData || isLoadingRecurringInvoice

  const recurringInvoiceDraftValues: InvoiceForm = useMemo(() => {
    // nothing consumes these while disabled, so skip loading the recurring invoice and
    // resolving the predefined values into it
    if (!enabled) {
      return getInvoiceFormDefaultValues()
    }

    const defaultInvoiceValues = getInvoiceFormDefaultValues({
      defaultContact,
      isRecurringInvoice: true,
      isStripeCardPaymentActive,
      predefinedInvoiceValues,
    })

    let values = defaultInvoiceValues

    if (!isLoading && recurringInvoice) {
      values = getInvoiceEditorFormValuesFromDraft({
        draft: recurringInvoice.draft,
        defaultInvoiceValues,
        getFilteredVatRates,
        isRecurringInvoice: true,
        vatRates,
      })
    }

    if (organization?.isVatExempted) {
      return getVatExemptedFormValues(values, defaultInvoiceValues)
    }

    return values
  }, [
    defaultContact,
    enabled,
    getFilteredVatRates,
    isLoading,
    isStripeCardPaymentActive,
    organization?.isVatExempted,
    predefinedInvoiceValues,
    recurringInvoice,
    vatRates,
  ])

  const recurringInvoiceValues = useMemo(() => {
    if (!enabled) {
      return undefined
    }

    const recurringInvoiceDefaultValues = getRecurringInvoiceFormDefaultValues({
      isStripeCardPaymentActive,
      t,
      organizationName: organization?.name || '',
    })

    const schedule = getRecurringInvoiceFormValuesFromSchedule({
      schedule: recurringInvoice?.schedule,
      defaultValues: recurringInvoiceDefaultValues,
    })

    // The id decides whether saving updates the profile or creates a new one, so a duplicate
    // must not carry the one it was copied from - it saves as its own profile.
    const isDuplicating = invoiceEditorVariant === InvoiceEditorVariant.RecurringInvoiceDuplicate
    const shouldKeepSourceId = !!documentId && !isDuplicating

    return {
      ...schedule,
      ...(shouldKeepSourceId ? { id: Number.parseInt(documentId, 10) } : {}),
    }
  }, [
    documentId,
    enabled,
    invoiceEditorVariant,
    isStripeCardPaymentActive,
    organization?.name,
    recurringInvoice?.schedule,
    t,
  ])

  return { invoiceValues: recurringInvoiceDraftValues, isLoading, predefinedInvoiceValues, recurringInvoiceValues }
}
